<template>
    <section class="tw:p-0">
        <sticky-header v-if="showHeader" class="tw:mt-4" background-color="background">
            <e-tooltip :label="title ? title : `Untitled ${formatType()}`">
                {{ title ? title : `Untitled ${formatType(true)}` }}
            </e-tooltip>

            <template #subtitle>
                <div v-if="versionNumber">
                    <e-tooltip :label="`${formatType(true)} version number`" position="is-left">
                        <e-taglist attached>
                            <e-tag size="small" class="has-text-weight-bold">#{{ originalVersionId }}</e-tag>
                            <e-tag size="small" type="is-primary" class="has-text-weight-bold">v{{ versionNumber }}</e-tag>
                        </e-taglist>
                    </e-tooltip>
                </div>
            </template>

            <template #actions>
                <div class="tw:flex tw:flex-col tw:xl:flex-row tw:xl:justify-end tw:items-center tw:gap-2 tw:xl:gap-1 tw:w-full">
                    <auto-save />

                    <e-button
                        type="button"
                        outlined
                        severity="info"
                        size="small"
                        icon-right="pi pi-angle-down"
                        class="tw:w-full tw:xl:w-fit"
                        @click="toggleActionsPopover"
                    >
                        <span>Actions</span>
                    </e-button>
                    <Popover ref="actionsPopover" @hide="onActionsPopoverHide" @show="onActionsPopoverShow">
                        <div class="tw:flex tw:flex-col tw:gap-2 tw:min-w-40">
                            <e-button
                                :disabled="!titleIsValid || !id || disableHeader"
                                icon-left="pi pi-copy"
                                type="secondary"
                                outlined
                                @click="onCopyClick"
                            >
                                {{ translate('copy') }}
                            </e-button>
                            <e-button
                                :disabled="!titleIsValid || !id || disableHeader || !hasQuestions"
                                icon-left="pi pi-eye"
                                type="secondary"
                                outlined
                                @click="onPreviewClick"
                            >
                                Preview
                            </e-button>
                        </div>
                    </Popover>

                    <e-modal
                        v-model="isComponentModalActive"
                        has-modal-card
                        trap-focus
                        :can-cancel="false"
                        style="max-width: 100%; margin: auto"
                    >
                        <quick-quiz-view
                            :id="previewVersion ? previewVersion : Number(uuid)"
                            :title="title"
                            :has-quiz-attempt="hasQuizAttempt"
                            @close="isComponentModalActive = false"
                        />
                    </e-modal>

                    <e-dropdown
                        :disabled="!titleIsValid || !id || disableHeader"
                        position="is-bottom-left"
                        trap-focus
                        :mobile-modal="false"
                        class="tw:w-full tw:xl:w-fit"
                    >
                        <template #trigger>
                            <e-button type="is-primary" icon-right="angle-down" outlined class="tw:w-full" size="small">
                                <span>Add Question</span>
                            </e-button>
                        </template>

                        <e-dropdown-item
                            v-for="item in questionTypes"
                            :key="item.value"
                            @click="addQuestion(item.value)"
                            @keyup="addQuestion(item.value)"
                        >
                            {{ item.name }}
                        </e-dropdown-item>
                    </e-dropdown>

                    <e-button type="is-primary" size="small" class="tw:w-full tw:xl:w-fit" @click="close" @keyup="close"> Finish</e-button>
                </div>
            </template>
        </sticky-header>

        <e-tabs type="is-boxed" class="column quiz-builder__tabs p-none">
            <e-tab-item :visible="canEditAQuiz" label="Editor">
                <quiz-editor-body
                    :uuid="uuid"
                    :is-new="isNew"
                    :is-quiz-ai-quiz-generation-result="isQuizAiQuizGenerationResult"
                    @has-questions="checkQuestions"
                />
            </e-tab-item>
        </e-tabs>

        <teleport to="body">
            <e-modal
                v-model="isQuizVersionModalOpen"
                custom-class="z-index-101"
                :can-cancel="false"
                has-modal-card
                trap-focus
            >
                <quiz-version-modal-form
                    :quiz="quizForModal"
                    @preview="previewQuiz(uuid)"
                    @close="isQuizVersionModalOpen = false"
                />
            </e-modal>
        </teleport>
    </section>
</template>

<script>
import filter from 'lodash/filter.js';
import has from 'lodash/has.js';
import isNull from 'lodash/isNull.js';
import Popover from 'primevue/popover';
import uuidV4 from 'uuid/v4.js';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { createNamespacedHelpers, mapState as mapGlobalState } from 'vuex';

import QuickQuizView from '@/components/activity-types/course-activity-types/activities/quiz/QuickQuizView.vue';
import AutoSave from '@/components/auto-save/AutoSave.vue';
import StickyHeader from '@/components/elements/StickyHeader.vue';
import QuizEditorBody from '@/components/quiz-builder/quiz-editor/QuizEditorBody.vue';
import EButton from '@/components/ui/EButton.vue';
import EDropdownItem from '@/components/ui/EDropdownItem.vue';
import QuizVersionModalForm from '@/components/version/QuizVersionModalForm.vue';
import GroupPermissionMixin from '@/mixins/GroupPermission.mixin.js';
import QuizTypeMixin from '@/mixins/QuizType.mixin.js';
import Course from '@/models/Course.js';
import Quiz from '@/models/Quiz.js';
import QuizHelper from '@/plugins/autosaver/quizzes/quizzes.js';
import axios from '@/plugins/axios.js';

const { mapState } = createNamespacedHelpers('QuizBuilder');

const computedField = field => {
    return {
        get() {
            return this.get(field);
        },

        set(value) {
            this.set({ field, value });
        },
    };
};

export default {
    name: 'QuizEditor',

    components: {
        EDropdownItem,
        EButton,
        QuizVersionModalForm,
        QuizEditorBody,
        StickyHeader,
        AutoSave,
        QuickQuizView,
        Popover,
    },

    mixins: [QuizTypeMixin, GroupPermissionMixin],

    props: {
        uuid: {
            type: [String, Number],
            required: true,
        },

        isNew: {
            type: Boolean,
            required: false,
        },

        showHeader: {
            type: Boolean,
            default: true,
        },

        isQuizAiQuizGenerationResult: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['close-editor', 'created'],

    data: () => ({
        isQuizVersionModalOpen: false,
        hover: false,
        disableHeader: false,
        quizActivityCount: 0,
        launchQuizLoading: false,
        isComponentModalActive: false,
        previewVersion: null,
        hasQuestions: false,
    }),

    setup() {
        const { t: translate } = useI18n();
        const actionsPopover = ref(null);
        const actionsPopoverVisible = ref(false);

        const toggleActionsPopover = event => {
            if (actionsPopover.value) {
                const target = event?.currentTarget || event?.target;
                if (actionsPopoverVisible.value) {
                    actionsPopover.value.hide();
                } else {
                    actionsPopover.value.show(event, target);
                }
            }
        };

        const hideActionsPopover = () => {
            if (actionsPopover.value) {
                actionsPopover.value.hide();
            }
        };

        const onActionsPopoverShow = () => {
            actionsPopoverVisible.value = true;
        };

        const onActionsPopoverHide = () => {
            actionsPopoverVisible.value = false;
        };

        return {
            translate,
            actionsPopover,
            actionsPopoverVisible,
            toggleActionsPopover,
            hideActionsPopover,
            onActionsPopoverShow,
            onActionsPopoverHide,
        };
    },

    computed: {
        ...mapGlobalState('Auth', { user: state => state.user }),

        ...mapState({
            questionTypes(state) {
                if (this.type && !this.type.includes('quiz')) {
                    return state.question_types;
                }

                return filter(state.question_types, type => !type.name.includes('Free'));
            },
        }),

        id: computedField('id'),

        title: {
            get() {
                const titleValue = this.get('title');
                // Ensure we return empty string for null/undefined values for proper validation
                return titleValue || '';
            },
            set(value) {
                // Ensure value is a string before calling trim
                const stringValue = String(value || '');
                const trimmedValue = stringValue.trim();
                this.set({ field: 'title', value: trimmedValue });
            },
        },

        type: computedField('type'),

        quizVersionId: computedField('quiz_version_id'),

        versionNumber: computedField('quiz_version_number'),

        originalVersionId: computedField('original_version_id'),

        parentUuid: computedField('activity_uuid'),

        groups: computedField('groups'),

        hasQuizAttempt: computedField('has_quiz_attempt'),

        course() {
            return this.$route.name === 'course-template-edit' ? false : Course.find(this.$route.params.courseId);
        },

        courseEditor() {
            return this.$route.name.includes('course');
        },

        canEditAQuiz() {
            if (this.user.is_admin || this.user.is_super_admin) {
                return true;
            }

            if (this.type === 'survey') {
                return this.checkSurveyPermissions();
            }

            if (this.hasGroupPermissions('edit a quiz')) {
                if (this.isQuizAiQuizGenerationResult && (!this.groups || this.groups.length === 0)) {
                    return true;
                }

                if (this.course && this.user.hasPermissionViaCourseTo(this.course, 'edit a quiz')) {
                    return true;
                }

                // check if asset is in the same group with user
                const quiz = {
                    id: this.id,
                    title: this.title,
                    type: this.type,
                    quizVersionId: this.quizVersionId,
                    versionNumber: this.versionNumber,
                    originalVersionId: this.originalVersionId,
                    parentUuid: this.parentUuid,
                    groups: this.groups,
                };

                return this.user.isInAssetGroup(quiz);
            }

            return false;
        },

        quizForModal() {
            return {
                id: this.id,
                title: this.title,
                type: this.type,
                quizVersionId: this.quizVersionId,
                versionNumber: this.versionNumber,
                originalVersionId: this.originalVersionId,
                parentUuid: this.parentUuid,
                groups: this.groups,
            };
        },

        canViewQuizActivityUsages() {
            if (this.user.is_admin || this.user.is_super_admin) {
                return true;
            }

            if (this.hasGroupPermissions('view quiz activity usages')) {
                // Special case: Allow Content Editors to view usages for AI-generated quizzes without group restrictions
                if (this.isQuizAiQuizGenerationResult && (!this.groups || this.groups.length === 0)) {
                    return true;
                }

                // check if asset is in the same group with user
                const quiz = {
                    id: this.id,
                    title: this.title,
                    type: this.type,
                    quizVersionId: this.quizVersionId,
                    versionNumber: this.versionNumber,
                    originalVersionId: this.originalVersionId,
                    parentUuid: this.parentUuid,
                    groups: this.groups,
                };

                return this.user.isInAssetGroup(quiz);
            }

            return false;
        },

        titleIsValid() {
            return this.title && typeof this.title === 'string' && this.title.trim().length > 0;
        },
    },

    watch: {
        async id(event) {
            if (event && (this.$route.name.includes('view') || this.$route.name.includes('index'))) {
                window.history.pushState({}, null, `/quizzes/${event}`);
            }

            if (event && !this.courseEditor) {
                await this.getQuizActivityCount();
            }
        },
    },

    async created() {
        const helper = new QuizHelper(this.$store, { payload: { target: '' } });

        const isAuthorized = this.checkAccess();

        if (!isAuthorized) {
            await this.redirectToAccessDenied();
            return;
        }

        if (this.id && !this.courseEditor) {
            this.quizActivityCount = await this.getQuizActivityCount();
        }

        if (has(this.$route.params, 'quizId') && Number(this.$route.params.quizId) !== Number(this.quizVersionId)) {
            window.history.pushState({}, null, `${this.quizVersionId}`);

            await this.$router.push({ params: { quizId: this.quizVersionId } });

            if (Number(this.id) !== Number(this.quizVersionId)) {
                const quiz = this.$store.getters['QuizBuilder/get'](this.id);

                const meta = { versionable_id: this.quizVersionId };

                await helper.updateVersion({ quiz, meta });
            }
        }

        if (this.hasQuizAttempt) {
            this.isQuizVersionModalOpen = true;
        }
    },

    unmounted() {
        if (this.$store.getters['QuizBuilder/get'](this.id)) {
            this.$store.dispatch('QuizBuilder/remove', this.uuid);
        }
    },

    methods: {
        isNull,

        onCopyClick() {
            this.hideActionsPopover();
            this.confirmDuplicate();
        },

        onPreviewClick() {
            if (!this.hasQuestions) return;
            this.hideActionsPopover();
            this.previewQuiz(isNaN(Number(this.uuid)) ? this.id : Number(this.uuid));
        },

        incrementRefreshKey() {
            Quiz.commit(state => {
                state.refresh_key++;
            });
        },

        addQuestion(type) {
            this.$store.dispatch(`QuizBuilder/${this.uuid}/addQuestion`, {
                type,
            });
        },

        getQuizActivityCount() {
            return this.$store.dispatch(`QuizBuilder/${this.uuid}/getQuizActivityCount`);
        },

        async close() {
            const loading = this.$loading.open({});

            if (!this.$route.name.includes('index')) {
                this.setToActivity();
            }

            if (this.id) {
                this.$emit('created', this.id);
            }

            this.incrementRefreshKey();

            this.$emit('close-editor');

            loading.close();
        },

        get(field) {
            const getterFunction = this.$store.getters[`QuizBuilder/${this.uuid}/get`];
            return getterFunction ? getterFunction(field) : null;
        },

        set({ field, value }) {
            this.$store.dispatch(`QuizBuilder/${this.uuid}/setQuizField`, {
                field,
                value,
            });
        },

        confirmDuplicate() {
            this.$dialog.prompt({
                message: `${this.translate('confirm_duplicate_quiz')}:`,
                inputAttrs: {
                    placeholder: this.translate('confirm_duplicate_quiz_placeholder'),
                    value: `${this.title} Copy`,
                },
                trapFocus: true,
                onConfirm: title => this.duplicate(title),
                confirmText: this.translate('copy'),
            });
        },

        async duplicate(title) {
            await axios.post(`/api/quizzes/${this.id}/duplicate`, { title });

            await this.$store.dispatch('Quizzes/paginateAndFetch');

            await this.close();
        },

        toggleHover(event) {
            if (event === 'in') {
                this.hover = true;

                return;
            }

            this.hover = false;
        },

        setToActivity() {
            const template = this.$route.name === 'course-template-edit';
            const namespace = template ? 'ActivityTemplates' : 'Activities';
            const parent = this.parentUuid ? this.parentUuid : this.uuid;
            const id = this.quizVersionId ? this.quizVersionId : this.get('id');

            if (this.quizVersionId && this.isNew) {
                this.$store.dispatch(`${namespace}/${parent}/quizContent`, {
                    field: 'quiz_id',
                    value: id,
                });

                this.$store.dispatch(`${namespace}/${parent}/quizContent`, {
                    field: 'quiz_version_id',
                    value: id,
                });
            }

            // Trigger item header to update
            this.$store.commit(`${namespace}/${parent}/EDIT_FIELD`, {
                field: 'updateKey',
                value: uuidV4(),
            });
        },

        checkQuestions(questions) {
            this.hasQuestions = questions;
        },

        checkAccess() {
            if (this.user.is_admin || this.user.is_super_admin) {
                return true;
            }

            if (this.hasGroupPermissions('edit a quiz')) {
                // Special case 1: Allow Content Editors to edit AI-generated quizzes without group restrictions
                if (this.isQuizAiQuizGenerationResult && (!this.groups || this.groups.length === 0)) {
                    return true;
                }

                // Special case 2: Allow Content Editors to edit ungrouped quizzes they created
                if ((!this.groups || this.groups.length === 0) && this.get('created_by') === this.user.id) {
                    return true;
                }

                // check if asset is in the same group with user
                const quiz = {
                    id: this.id,
                    title: this.title,
                    type: this.type,
                    quizVersionId: this.quizVersionId,
                    versionNumber: this.versionNumber,
                    originalVersionId: this.originalVersionId,
                    parentUuid: this.parentUuid,
                    groups: this.groups,
                };

                return this.user.isInAssetGroup(quiz);
            }

            return this.$can('edit', 'quiz');
        },

        async previewQuiz(version) {
            this.previewVersion = Number(version);
            this.isComponentModalActive = true;
        },

        checkSurveyPermissions() {
            const quiz = {
                id: this.id,
                title: this.title,
                type: this.type,
                quizVersionId: this.quizVersionId,
                versionNumber: this.versionNumber,
                originalVersionId: this.originalVersionId,
                parentUuid: this.parentUuid,
                groups: this.groups,
            };

            if (this.hasGroupPermissions('edit a survey')) {
                return this.user.isInAssetGroup(quiz);
            }

            return this.user.hasPermissionTo('edit a survey');
        },
    },
};
</script>

<!--suppress CssUnusedSymbol -->
<style>
.z-index-101 {
    z-index: 101 !important;
}
</style>
